import React, { useRef, useState } from 'react';
import { Button, Modal, Form, Radio, DatePicker, notification } from 'antd';
import { Formik, Field } from 'formik';
import { get } from 'lodash';
// import useDownloadFile from 'hooks/useDownloadFile';
import moment from 'moment';
import classes from './ExportData.module.scss';
import { useLazyExportInspectionDataQuery } from 'app/features/inspection/api';
import { getValidateExportData } from './validator';
import { useDownloadFile } from 'app/hooks/useDownloadFile';

const { RangePicker } = DatePicker;
/**
 * The ExportData component renders a button to export data and a modal with form fields for selecting
 * content and date range.
 * Note: Using Antd Form as as Form Layout
 * @returns The `ExportData` component is returning a React Fragment containing a Button component with
 * the text "Export Data" and a Modal component with the title "Export". Inside the Modal component,
 * there is a Formik component with initial values set to an empty object. Within the Formik component,
 * there is a Form component with two Field components.
 */

export const ExportData = () => {
  const [open, setOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [previousValue, setPreviousValue] = useState({
    content: '',
    range: undefined
  });
  const [fetch] = useLazyExportInspectionDataQuery();
  const { downloadWithLink } = useDownloadFile();
  const form = useRef(null);

  const createFileName = (range, content) => {
    const fileName = ['IQC'];
    if (content === 'jobList') {
      fileName.push('JobList');
    } else {
      fileName.push('ReportDetails');
    }
    fileName.push(
      `${range[0].format('YYYYMMDD')}-${range[1].format('YYYYMMDD')}`
    );
    fileName.push(`${moment().format('YYYYMMDD_HHmm')}`);
    return fileName.join('_');
  };

  const exportData = async (values) => {
    const { range, content } = values;
    setProcessing(true);
    try {
      const response = await fetch({
        startDate: range[0].format('YYYY-MM-DD'),
        endDate: range[1].format('YYYY-MM-DD'),
        content
      }).unwrap();

      const data = get(response, 'data');
      const { buffer } = new Uint8Array(data);
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      setPreviousValue({ ...values });
      downloadWithLink(createFileName(range, content), url);
      setProcessing(false);
    } catch (error) {
      setProcessing(false);
      notification.error({
        message: 'Export Data Failed'
      });
    }
  };
  return (
    <React.Fragment>
      <Button
        size="large"
        type="primary"
        onClick={() => {
          setOpen(true);
        }}
      >
        Export Data
      </Button>
      <Modal
        open={open}
        confirmLoading={processing}
        title="Export"
        onCancel={() => {
          setOpen(false);
        }}
        cancelButtonProps={{ hidden: true }}
        onOk={() => {
          form.current.submitForm();
        }}
      >
        <Formik
          initialValues={previousValue}
          enableReinitialize
          validationSchema={getValidateExportData()}
          onSubmit={exportData}
        >
          {(formikProps) => {
            form.current = formikProps;
            return (
              <Form className={classes.form} layout="vertical">
                {/* Form fields go here */}
                <Field name="content">
                  {({ field, meta }) => {
                    return (
                      <Form.Item
                        label="Content"
                        validateStatus={meta.error ? 'error' : ''}
                        help={meta.error}
                      >
                        <Radio.Group
                          onChange={(e) => {
                            field.onChange({
                              target: { value: e.target.value, name: 'content' }
                            });
                          }}
                          value={field.value}
                          options={[
                            { value: 'jobList', label: 'Job List' },
                            { value: 'reportDetails', label: 'Report Details' }
                          ]}
                        />
                      </Form.Item>
                    );
                  }}
                </Field>
                <Field name="range">
                  {({ field, meta }) => {
                    return (
                      <Form.Item
                        label="Inspection Date"
                        validateStatus={meta.error ? 'error' : ''}
                        help={meta.error}
                      >
                        <RangePicker
                          onChange={(dates) => {
                            field.onChange({
                              target: {
                                name: 'range',
                                value: dates
                              }
                            });
                          }}
                          value={field.value}
                          format="DD/MM/YYYY"
                        />
                      </Form.Item>
                    );
                  }}
                </Field>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </React.Fragment>
  );
};
